// The privacy notice she gives her clients (docs/legal/privacy-notice-clients.md,
// Part 2), generated from her details. She is the controller, so it is in her
// voice; Harmovi appears as the service she uses. One body, two places: the
// booking site's Privacy page (headings at 2) and the sheet in her app where she
// checks what her clients are told (headings at 3, under the sheet's own title).
import React from 'react'

export interface ClientNoticeData {
  trading_name: string
  professional_name: string
  location: string
  phone: string | null
  retention_years: number
}

// ClientNotice is everything after the page's title: the opening paragraph and
// each section. The caller supplies the title and the wrapper.
export default function ClientNotice({ notice, level }: { notice: ClientNoticeData; level: 2 | 3 }) {
  const H = `h${level}` as 'h2' | 'h3'
  const years = notice.retention_years
  return (
    <>
      <p>
        I’m {notice.professional_name}, trading as {notice.trading_name}{notice.location ? `, ${notice.location}` : ''}. I’m responsible for
        the information I keep about you.{notice.phone && <> Questions: {notice.phone}.</>}
      </p>

      <H>What I keep, and why</H>
      <ul>
        <li><strong>Your contact details and appointments</strong>, to arrange and remind you of your appointments, because we have an agreement for your treatment. I may email you a link to confirm your email address is yours.</li>
        <li><strong>Payments</strong>, because the law requires me to keep financial records.</li>
        <li>
          <strong>Treatment notes and health information you share with me</strong>, such as injuries, pregnancy, allergies,
          medication and conditions, so your treatment is safe and suited to you. <strong>I only keep these with your explicit
          agreement, which I record. You can withdraw it at any time.</strong> If you do, I’ll stop adding notes, and that may
          limit the treatments I can safely offer. I’ll keep the notes I already have only as long as I’d need them to answer
          a claim about your treatment, then delete them.
        </li>
        <li>
          <strong>Photos of the area I treated</strong>, only if you agree first, and only if I think they help me keep an
          accurate record of your treatment. I record your agreement and when you gave it, and you can withdraw it at any
          time; if you do, I’ll stop taking photos and remove the ones I no longer need. They are kept with your treatment
          notes, on the same terms, in private storage that only I can reach, with the hidden details in the photo (such as
          where it was taken) removed. Photos are health information. Because you agreed to it.
        </li>
        <li>
          <strong>The consultation form you fill in and sign</strong>, if I ask you for one, as a record of what you told me
          and agreed to before your treatment. It keeps your answers, which may include health information, your typed full
          name, your signature if you draw one (an image), when you signed, the email address the link was sent to, your
          browser type and, if you’re under 18, your parent or guardian’s name and how they’re related to you. Your IP
          address isn’t kept. I send the link only to the email address I have for you, and the emails
          never contain your answers. The form is kept with your treatment notes, on the same terms.
        </li>
        <li>
          <strong>A saved card</strong>, only if you ticked “Save my card” when you paid, or agreed when I sent you a payment
          link. I keep its <strong>brand, last four digits and expiry</strong>, and a record of the wording you agreed to and
          when, so I can take payment for an appointment you’ve had or a package you ask to buy, for the amount due, and
          nothing else. You get an email each time I charge it. The card itself is held by <strong>Stripe</strong>, not by
          me or Harmovi, and I can’t see the full number. It stays until you ask me to remove it or I delete your records,
          and I’ll remove it when you ask. Because you agreed to it.
        </li>
        <li><strong>Offers and news</strong>, only if you said yes. You can stop any time.</li>
      </ul>

      <H>Who helps me</H>
      <p>
        I use <strong>Harmovi</strong> to run my appointments and keep my records. Harmovi stores them securely for me and can’t use
        them for anything else. Harmovi’s providers are Cloudflare (hosting, storage and email) and, for card payments,
        Stripe. Your answers are stored by Harmovi and your signature image is kept privately; only you and I can see them. After
        you sign, I email you a link to a read-only copy of your form, so you have your own copy.{' '}
        <strong>Your health information is never sent to an AI service.</strong> If I take card payments, Stripe processes
        them, and holds a card you asked me to save.
      </p>

      <H>How long I keep it</H>
      <p>
        Treatment notes, treatment photos and consultation forms: {years} {years === 1 ? 'year' : 'years'} after your last appointment, and for
        anyone under 18, at least until they turn 25. Payment records: 6 years, as the law requires. Then they’re deleted.
        A saved card: until you ask me to remove it, or I delete your records. If you ask me to delete your records, your
        forms, signature and photos are deleted with them.
      </p>

      <H>Your rights</H>
      <p>
        You can ask me to see, correct or delete your information, or to stop using it for something. Just ask me. If you’re
        unhappy with how I’ve handled your information, tell me first and I’ll reply. You can also complain to the
        Information Commissioner’s Office: <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ico.org.uk</a>, 0303 123 1113.
      </p>
    </>
  )
}
